import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  pinnedActionsRecipe,
  pinnedActionsRowRecipe,
  type PinnedActionsRecipeProps,
} from '../recipes/surface.recipe';

export interface PinnedActionsProps
  extends React.HTMLAttributes<HTMLDivElement>,
    PinnedActionsRecipeProps {}

/**
 * A detail card's buttons, pinned to the top of its scrolling column.
 *
 *   <Card><CardBody><Stack gap={6}>
 *     <PinnedActions collapsed={!warning}>
 *       <Button size="xs" pill variant="outline">Print</Button>
 *     </PinnedActions>
 *     {warning}
 *     …title, sections…
 *
 * First in the column, so it can travel the column's height. `collapsed`
 * (the default) gives it no height and takes the column's gap back, which
 * lands the buttons on the title line; turn it off when something sits
 * between them and the title, so they keep a line of their own.
 *
 * Collapsed, the title block beside them needs a width cap so a long name
 * does not run under them, and `md:max-w-[calc(100%-20rem)]` is the room a
 * full-width report card leaves. Read that cap carefully before reaching for
 * it: the 100% is the COLUMN, not the window, so in any column narrower than
 * 20rem the subtraction goes negative, the cap clamps to zero and the title
 * is crushed to one word a line. The `md:` is no guard — it asks the window.
 * Where a column can be narrow, and a split view or an embedded card always
 * can, pass `collapsed={false}` and spend the row instead.
 */
export const PinnedActions = React.forwardRef<
  HTMLDivElement,
  PinnedActionsProps
>(function PinnedActions({ collapsed, className, children, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(pinnedActionsRecipe({ collapsed }), className)}
      {...props}
    >
      <div className={pinnedActionsRowRecipe()}>{children}</div>
    </div>
  );
});
